import { useEffect, useState } from 'react';
import { ShoppingCart, Trash2, ChevronDown, ChevronUp, Plus } from 'lucide-react';

import { useShoppingListStore, selectUnpurchasedCount, selectItemsByCategory } from '@/stores/shopping-list-store';
import { usePropertyStore } from '@/stores/property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { ShoppingItemForm } from '@/components/shopping/ShoppingItemForm';
import { OperationalRow } from '@/components/common/OperationalRow';
import { CircleCheckbox } from '@/components/common/CircleCheckbox';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { SHOPPING_CATEGORIES } from '@/types/shopping-list';
import type { ShoppingListItem, CreateShoppingListItemInput } from '@/types/shopping-list';

import noShoppingItemsPng from '@/assets/illustrations/empty/no-shopping-items.png';

const STARTER_ITEM_LIMIT = 20;

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  SHOPPING_CATEGORIES.map(({ value, label }) => [value, label]),
);

function ItemRow({
  item,
  propertyName,
  onToggle,
  onEdit,
  onDelete,
}: {
  item: ShoppingListItem;
  propertyName?: string;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const status = item.purchased ? 'purchased' : 'pending';

  return (
    <OperationalRow
      status={status}
      data-testid={`shopping-item-${item.id}`}
    >
      {/* Purchased toggle — sibling of content, not nested */}
      <div data-slot="checkbox">
        <CircleCheckbox
          checked={item.purchased}
          onChange={() => onToggle()}
          label={item.purchased ? 'Mark unpurchased' : 'Mark purchased'}
          inputTestId={`toggle-purchased-${item.id}`}
        />
      </div>

      {/* Item info */}
      <div className="flex-1 min-w-0 flex items-start gap-2 px-3 py-3.5" data-slot="content">
        <div className="flex-1 min-w-0">
          <p
            className={`text-body-sm font-medium truncate ${
              item.purchased
                ? 'line-through text-[var(--color-text-muted)]'
                : 'text-[var(--color-text-primary)]'
            }`}
            data-state={item.purchased ? 'purchased' : 'pending'}
          >
            {item.name}
          </p>
          {(item.quantity != null || item.unit || propertyName) && (
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {(item.quantity != null || item.unit) && (
                <span className="text-body-xs text-[var(--color-text-muted)]">
                  {item.quantity != null ? item.quantity : ''}{item.quantity != null && item.unit ? ' ' : ''}{item.unit ?? ''}
                </span>
              )}
              {propertyName && (
                <span className="text-body-xs text-[var(--color-text-muted)] bg-[var(--color-surface)] border border-[var(--color-border)] px-1.5 py-0.5 rounded-sm">
                  {propertyName}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0 pr-2" data-slot="actions">
        <button
          type="button"
          onClick={onEdit}
          className="p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors"
          aria-label="Edit item"
          data-testid={`edit-item-${item.id}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-950/30 transition-colors"
          aria-label="Delete item"
          data-testid={`delete-item-${item.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </OperationalRow>
  );
}

export function ShoppingListPage() {
  const { items, isLoading, loadItems, addItem, updateItem, togglePurchased, deleteItem } =
    useShoppingListStore();
  const { properties, loadProperties } = usePropertyStore();
  const isPro = useUserProfileStore((s) => s.isPro);

  const [filterPropertyId, setFilterPropertyId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ShoppingListItem | undefined>(undefined);
  const [showPurchased, setShowPurchased] = useState(false);
  const [showShoppingGate, setShowShoppingGate] = useState(false);

  useEffect(() => {
    void loadItems(filterPropertyId || undefined);
  }, [filterPropertyId, loadItems]);

  useEffect(() => {
    if (properties.length === 0) void loadProperties();
  }, [properties.length, loadProperties]);

  const unpurchasedCount = selectUnpurchasedCount(items);
  const itemsByCategory = selectItemsByCategory(items);
  const purchasedItems = items.filter((i) => i.purchased);

  const categoryOrder = SHOPPING_CATEGORIES.map((c) => c.value);
  const sortedCategories = Object.keys(itemsByCategory).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b),
  );

  function getPropertyName(propertyId?: string): string | undefined {
    if (!propertyId) return undefined;
    return properties.find((p) => p.id === propertyId)?.name;
  }

  async function handleSubmit(data: CreateShoppingListItemInput) {
    if (editingItem) {
      await updateItem(editingItem.id, data);
    } else {
      // Gate: Starter users limited to 20 items
      if (!isPro() && items.length >= STARTER_ITEM_LIMIT) {
        setShowShoppingGate(true);
        return;
      }
      await addItem(data);
    }
  }

  function handleAddClick() {
    // Gate: block opening form if Starter is already at limit
    if (!isPro() && items.length >= STARTER_ITEM_LIMIT) {
      setShowShoppingGate(true);
      return;
    }
    setShowForm(true);
  }

  function openEdit(item: ShoppingListItem) {
    setEditingItem(item);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingItem(undefined);
  }

  return (
    <div className="flex flex-col gap-0 -mx-4 -mt-6">
      {/* Header */}
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 pt-6 pb-4">
        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <ShoppingCart size={20} className="text-[var(--color-accent)]" />
            <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
              Shopping List
            </h1>
            {unpurchasedCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--color-accent)] text-[10px] font-semibold text-white leading-none"
                data-testid="unpurchased-badge"
              >
                {unpurchasedCount}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleAddClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-accent)] text-white text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            data-testid="add-item-button"
          >
            <Plus size={16} />
            Add Item
          </button>
        </div>

        {/* Property filter */}
        {properties.length > 1 && (
          <div className="mt-3 max-w-2xl mx-auto">
            <select
              value={filterPropertyId}
              onChange={(e) => setFilterPropertyId(e.target.value)}
              className="w-full sm:w-auto px-3 py-1.5 rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              data-testid="property-filter"
            >
              <option value="">All Properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-5 flex flex-col gap-4 max-w-2xl mx-auto w-full">
        {isLoading ? (
          <div className="flex flex-col gap-2 pt-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-10 rounded-lg bg-[var(--color-elevated)] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          /* Empty state */
          <OperationalGuide
            illustration={noShoppingItemsPng}
            heading="Your Gear List Is Empty"
            subtext="Add items to track what you need before the season."
            ctaLabel="Add First Item"
            onCtaClick={handleAddClick}
            data-guide-id="shopping-list-empty"
          />
        ) : (
          <>
            {/* Unpurchased items grouped by category */}
            {sortedCategories.length === 0 && purchasedItems.length > 0 ? null : (
              sortedCategories.map((cat) => (
                <div key={cat} className="flex flex-col gap-0">
                  <p className="field-label mb-1.5">
                    {CATEGORY_LABELS[cat] ?? cat}
                  </p>
                  <div className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-lg overflow-hidden">
                    {itemsByCategory[cat].map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        propertyName={getPropertyName(item.propertyId)}
                        onToggle={() => void togglePurchased(item.id)}
                        onEdit={() => openEdit(item)}
                        onDelete={() => void deleteItem(item.id)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}

            {/* Purchased items (collapsed section) */}
            {purchasedItems.length > 0 && (
              <div className="flex flex-col gap-0">
                <button
                  type="button"
                  onClick={() => setShowPurchased((v) => !v)}
                  className="flex items-center gap-2 field-label mb-1.5 hover:text-[var(--color-text-secondary)] transition-colors"
                  data-testid="toggle-purchased-section"
                >
                  {showPurchased ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  Purchased ({purchasedItems.length})
                </button>
                {showPurchased && (
                  <div className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-lg overflow-hidden">
                    {purchasedItems.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        propertyName={getPropertyName(item.propertyId)}
                        onToggle={() => void togglePurchased(item.id)}
                        onEdit={() => openEdit(item)}
                        onDelete={() => void deleteItem(item.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <ShoppingItemForm
          item={editingItem}
          defaultPropertyId={filterPropertyId || undefined}
          onSubmit={handleSubmit}
          onClose={closeForm}
        />
      )}

      {showShoppingGate && (
        <UpsellSheet
          featureName={UPSELL_COPY.shopping_list_limit.featureName}
          description={UPSELL_COPY.shopping_list_limit.description}
          featureGate="shopping_list_limit"
          onDismiss={() => setShowShoppingGate(false)}
        />
      )}
    </div>
  );
}
